/**
 * Member Achievements Panel — Demo Flow 7.
 *
 * Renders earnings, badges, achievement levels, and gift card history for
 * one member. Drops into MemberProfile / MemberIncentiveDashboard.
 */
import React, { useEffect, useState } from 'react';
import { authFetchJson } from '../../lib/authFetch';
import { IncentiveCompletionTrigger } from '../communications/EngagementDemoFlowComponents';

interface Earning {
  id: number;
  earning_number: string;
  incentive_type: 'MONETARY' | 'BADGE' | 'ACHIEVEMENT_LEVEL' | 'POINTS' | 'RECOGNITION' | 'REWARD_ITEM' | 'SERVICE_CREDIT';
  monetary_amount: number | null;
  currency: string | null;
  badge_name: string | null;
  achievement_level: string | null;
  status: 'PENDING' | 'EARNED' | 'APPROVED' | 'PROCESSING' | 'PAID' | 'REVERSED' | 'DENIED' | 'EXPIRED';
  earned_date: string | null;
  paid_date: string | null;
  giftcard_order_id: number | null;
  delivery_channel: string | null;
  configuration_name: string | null;
}

interface Giftcard {
  id: number;
  amount_cents: number;
  currency: string;
  status: string;
  redemption_url: string | null;
  redemption_code_masked: string | null;
  sent_at: string | null;
  redeemed_at: string | null;
}

interface DashboardResponse {
  member_id: number;
  earnings: Earning[];
  giftcards: Giftcard[];
  summary: {
    total_earned_usd: number;
    badges_count: number;
    achievement_levels: string[];
  };
}

interface Props {
  memberId: number;
  cboId?: number;
  refreshKey?: number;
}

const STATUS_COLOR: Record<string, string> = {
  PENDING:    'bg-yellow-100 text-yellow-700',
  EARNED:     'bg-blue-100 text-blue-700',
  APPROVED:   'bg-purple-100 text-purple-700',
  PROCESSING: 'bg-orange-100 text-orange-700',
  PAID:       'bg-green-100 text-green-700',
  REVERSED:   'bg-gray-100 text-gray-700',
  DENIED:     'bg-red-100 text-red-700',
  EXPIRED:    'bg-gray-100 text-gray-500',
};

export const MemberAchievementsPanel: React.FC<Props> = ({ memberId, cboId, refreshKey = 0 }) => {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [bumpKey, setBumpKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    authFetchJson<DashboardResponse>(`/api/incentives/members/${memberId}/dashboard`)
      .then(setData)
      .catch((e) => { console.warn('dashboard failed', e); setData(null); })
      .finally(() => setLoading(false));
  }, [memberId, refreshKey, bumpKey]);

  if (loading) return <p className="text-sm text-gray-500">Loading achievements…</p>;

  if (!data) {
    return (
      <div className="space-y-4">
        {cboId && (
          <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div className="text-sm text-emerald-900">
              <strong>No incentive earnings yet for this member.</strong> Trigger
              one below — auto-pay flips it straight to PAID with a mock gift card.
            </div>
            <IncentiveCompletionTrigger
              memberId={memberId}
              cboId={cboId}
              onCompleted={() => setBumpKey(k => k + 1)} />
          </div>
        )}
        <p className="text-sm text-gray-500">No earnings, badges, or gift cards on file for this member.</p>
      </div>
    );
  }

  const monetary = data.earnings.filter(e => e.incentive_type === 'MONETARY');
  const badges   = data.earnings.filter(e => e.incentive_type === 'BADGE');
  const levels   = data.earnings.filter(e => e.incentive_type === 'ACHIEVEMENT_LEVEL');

  return (
    <div className="space-y-6">
      {cboId && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <div className="text-sm text-emerald-900">
            <strong>Demo trigger:</strong> close a HEDIS gap for this member and watch the
            earning row appear below — auto-pay flips it straight to PAID with a mock gift card.
          </div>
          <IncentiveCompletionTrigger
            memberId={memberId}
            cboId={cboId}
            onCompleted={() => setBumpKey(k => k + 1)} />
        </div>
      )}

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-green-50 to-emerald-50 p-4">
          <div className="text-xs font-bold uppercase text-emerald-700">Total earned</div>
          <div className="mt-1 text-2xl font-bold text-emerald-900">
            ${data.summary.total_earned_usd.toFixed(2)}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-blue-50 to-sky-50 p-4">
          <div className="text-xs font-bold uppercase text-sky-700">Badges</div>
          <div className="mt-1 text-2xl font-bold text-sky-900">{data.summary.badges_count}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-purple-50 to-violet-50 p-4">
          <div className="text-xs font-bold uppercase text-violet-700">Levels</div>
          <div className="mt-1 line-clamp-2 text-sm font-semibold text-violet-900">
            {data.summary.achievement_levels.join(', ') || '—'}
          </div>
        </div>
      </div>

      {/* Badges */}
      {badges.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">Badges</h3>
          <div className="flex flex-wrap gap-2">
            {badges.map(b => (
              <div
                key={b.id}
                className="flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5"
              >
                <span aria-hidden>🏅</span>
                <span className="text-sm font-medium text-blue-900">{b.badge_name}</span>
                {b.earned_date && (
                  <span className="text-[10px] text-blue-600">
                    {new Date(b.earned_date).toLocaleDateString()}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Achievement levels */}
      {levels.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">Achievement levels</h3>
          <div className="space-y-2">
            {levels.map(l => (
              <div
                key={l.id}
                className="flex items-center gap-3 rounded-lg border border-purple-200 bg-purple-50 px-4 py-2"
              >
                <span aria-hidden className="text-xl">👑</span>
                <span className="text-sm font-semibold text-purple-900">{l.achievement_level}</span>
                <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_COLOR[l.status] || 'bg-gray-100'}`}>
                  {l.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monetary earnings */}
      <div>
        <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">Monetary earnings</h3>
        {monetary.length === 0 && <p className="text-sm text-gray-500">No monetary earnings yet.</p>}
        <div className="space-y-2">
          {monetary.map(e => (
            <div
              key={e.id}
              className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3"
            >
              <span aria-hidden className="text-2xl">💳</span>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-gray-900">
                  {e.configuration_name || `Earning ${e.earning_number}`}
                </div>
                <div className="text-xs text-gray-500">
                  Earned {e.earned_date ? new Date(e.earned_date).toLocaleDateString() : '—'}
                  {e.paid_date && ` · Paid ${new Date(e.paid_date).toLocaleDateString()}`}
                </div>
              </div>
              <div className="ml-auto flex items-center gap-3">
                <span className="text-sm font-bold text-emerald-700">
                  ${Number(e.monetary_amount || 0).toFixed(2)}
                </span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_COLOR[e.status] || 'bg-gray-100'}`}>
                  {e.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gift card history */}
      {data.giftcards.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">Gift cards</h3>
          <div className="space-y-2">
            {data.giftcards.map(g => (
              <div
                key={g.id}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm"
              >
                <span aria-hidden>🎁</span>
                <span className="font-medium text-gray-900">${(g.amount_cents / 100).toFixed(2)}</span>
                <span className="text-xs text-gray-500">{g.redemption_code_masked}</span>
                <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">
                  {g.status}
                </span>
                {g.redemption_url && (
                  <a
                    href={g.redemption_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded bg-blue-600 px-2 py-0.5 text-xs text-white hover:bg-blue-700"
                  >
                    Redeem
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberAchievementsPanel;
